// src/api/features/payment-links/api.ts
import { request } from "@/api/client";
import { PATHS } from "@/api/constants";
import type { PaymentLink, PaymentLinkDTO } from "./types";

/* =========================
 * Server shapes
 * ========================= */

type ServerPaymentLink = {
  id: number;
  linkCode: string;
  provider: string;
  status: PaymentLink["status"];
  orderId: string | null;
  amount: string;
  currencyCode: string;
  customerName: string | null;
  customerEmail: string | null;
  customerPhone: string | null;
  description: string;
  reference: string | null;
  checkoutUrl: string | null;
  refundedAmount: string | null;
  expiresAt: string | null;
  paidAt: string | null;
  cancelledAt: string | null;
  sentAt: string | null;
  sentCount: number | null;
  createdAt: string;
  createdBy: string | null;
};

type ApiEnvelope<T> = { ok: boolean; message?: string; data: T };
type ServerMeta = { page: number; pageSize: number; total: number };

function unwrap<T>(res: any): T {
  if (res && typeof res === "object" && "ok" in res && "data" in res) {
    return (res as ApiEnvelope<T>).data;
  }
  return res as T;
}

/** Human labels for the create-form fields, keyed by their payload name. */
const FIELD_LABELS: Record<string, string> = {
  amount: "Amount",
  currencyCode: "Currency",
  customerName: "Customer name",
  customerEmail: "Customer email",
  customerPhone: "Customer phone",
  description: "Purpose / description",
  reference: "Reference",
  expiresAt: "Expires at",
};

/**
 * Error carrying per-field validation messages so a form can render them next
 * to the right inputs (fieldErrors) and/or in a summary banner (message).
 */
export class PaymentLinkApiError extends Error {
  fieldErrors?: Record<string, string>;
  constructor(message: string, fieldErrors?: Record<string, string>) {
    super(message);
    this.name = "PaymentLinkApiError";
    this.fieldErrors = fieldErrors;
  }
}

function asMessage(err: unknown): PaymentLinkApiError {
  const e = err as any;
  const data = e?.response?.data;

  // Backend zod validation: { ok:false, message, errors:{ formErrors, fieldErrors } }
  const errors = data?.errors;
  if (errors && (errors.fieldErrors || errors.formErrors)) {
    const fieldErrors: Record<string, string> = {};
    for (const [key, msgs] of Object.entries(errors.fieldErrors ?? {})) {
      const first = Array.isArray(msgs) ? msgs[0] : String(msgs);
      if (first) fieldErrors[key] = String(first);
    }
    const formErrors: string[] = Array.isArray(errors.formErrors)
      ? errors.formErrors
      : [];
    const readableParts = [
      ...formErrors,
      ...Object.entries(fieldErrors).map(
        ([k, v]) => `${FIELD_LABELS[k] ?? k}: ${v}`
      ),
    ];
    const message = readableParts.length
      ? readableParts.join(" · ")
      : data?.message || "Please fix the highlighted fields.";
    return new PaymentLinkApiError(
      message,
      Object.keys(fieldErrors).length ? fieldErrors : undefined
    );
  }

  const message = data?.message || e?.message || "Request failed";
  return new PaymentLinkApiError(message);
}

function toModel(r: ServerPaymentLink): PaymentLink {
  return {
    id: r.id,
    linkCode: r.linkCode,
    provider: r.provider,
    status: r.status,
    orderId: r.orderId ?? null,
    amount: r.amount,
    currencyCode: r.currencyCode,
    customerName: r.customerName ?? null,
    customerEmail: r.customerEmail ?? null,
    customerPhone: r.customerPhone ?? null,
    description: r.description,
    reference: r.reference ?? null,
    checkoutUrl: r.checkoutUrl ?? null,
    refundedAmount: r.refundedAmount ?? null,
    expiresAt: r.expiresAt ?? null,
    paidAt: r.paidAt ?? null,
    cancelledAt: r.cancelledAt ?? null,
    sentAt: r.sentAt ?? null,
    sentCount: r.sentCount ?? 0,
    createdAt: r.createdAt,
    createdBy: r.createdBy ?? null,
  };
}

const base = PATHS.paymentLinks.base;
const byId = (id: number | string) => `${base}/${id}`;

/* =========================
 * API calls
 * ========================= */

export async function listPaymentLinks(
  params: PaymentLinkDTO.ListParams = {}
): Promise<PaymentLinkDTO.Page<PaymentLink>> {
  try {
    const { page = 1, pageSize = 20, q, status } = params;
    const qp = new URLSearchParams();
    qp.set("page", String(page));
    qp.set("pageSize", String(pageSize));
    if (q) qp.set("q", q);
    if (status) qp.set("status", status);

    const res = await request<
      (ApiEnvelope<ServerPaymentLink[]> & { meta?: ServerMeta }) | ServerPaymentLink[]
    >({ method: "GET", url: `${base}?${qp.toString()}` });

    const items = unwrap<ServerPaymentLink[]>(res);
    const meta = (res as any)?.meta as ServerMeta | undefined;

    return {
      items: (items ?? []).map(toModel),
      total: meta?.total ?? items?.length ?? 0,
      page: meta?.page ?? page,
      pageSize: meta?.pageSize ?? pageSize,
    };
  } catch (err) {
    throw asMessage(err);
  }
}

export async function getPaymentLink(id: number): Promise<PaymentLink> {
  try {
    const res = await request<ApiEnvelope<ServerPaymentLink> | ServerPaymentLink>({
      method: "GET",
      url: byId(id),
    });
    return toModel(unwrap<ServerPaymentLink>(res));
  } catch (err) {
    throw asMessage(err);
  }
}

export async function createPaymentLink(
  payload: PaymentLinkDTO.Create
): Promise<PaymentLink> {
  try {
    const res = await request<ApiEnvelope<ServerPaymentLink> | ServerPaymentLink>({
      method: "POST",
      url: base,
      data: payload,
    });
    return toModel(unwrap<ServerPaymentLink>(res));
  } catch (err) {
    throw asMessage(err);
  }
}

export async function resendPaymentLink(
  id: number,
  customerEmail?: string | null
): Promise<PaymentLink> {
  try {
    const res = await request<ApiEnvelope<ServerPaymentLink> | ServerPaymentLink>({
      method: "POST",
      url: `${byId(id)}/resend`,
      data: customerEmail ? { customerEmail } : {},
    });
    return toModel(unwrap<ServerPaymentLink>(res));
  } catch (err) {
    throw asMessage(err);
  }
}

export async function cancelPaymentLink(id: number): Promise<PaymentLink> {
  try {
    const res = await request<ApiEnvelope<ServerPaymentLink> | ServerPaymentLink>({
      method: "POST",
      url: `${byId(id)}/cancel`,
    });
    return toModel(unwrap<ServerPaymentLink>(res));
  } catch (err) {
    throw asMessage(err);
  }
}

export async function refreshPaymentLink(id: number): Promise<PaymentLink> {
  try {
    const res = await request<ApiEnvelope<ServerPaymentLink> | ServerPaymentLink>({
      method: "POST",
      url: `${byId(id)}/refresh`,
    });
    return toModel(unwrap<ServerPaymentLink>(res));
  } catch (err) {
    throw asMessage(err);
  }
}

export async function refundPaymentLink(
  id: number,
  payload: PaymentLinkDTO.Refund
): Promise<PaymentLink> {
  try {
    const res = await request<ApiEnvelope<ServerPaymentLink> | ServerPaymentLink>({
      method: "POST",
      url: `${byId(id)}/refund`,
      data: payload,
    });
    return toModel(unwrap<ServerPaymentLink>(res));
  } catch (err) {
    throw asMessage(err);
  }
}
